import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { considerUninitializedSocketDisconnected } from 'components/GlobalState/commands/considerUninitializedSocketDisconnected';
import { setSocketState } from 'components/GlobalState/commands/setSocketState';
import { selectAppLoading } from 'components/GlobalState/selectors/selectAppLoading';
import {
  SOCKET_CONNECTION_DELAY,
  connectSocket,
  isSocketConnected,
  socketConnection,
} from 'components/Socket/ipfSocket';
import { useEffect } from 'react';

/**
 * Consider socket disconnected if it does not connect within given amount of time
 *
 * Because sockets are pretty wild, socket state may be set to neither CONNECTED nor DISCONNECTED
 * when initial connection fails. So, indicate socket disconnection instead of stuck loading screen
 * after a reasonable timeout.
 */
const SOCKET_INITIAL_CONNECTION_TIMEOUT = 10000;

/**
 * Establish socket connection and project its state to GlobalState
 */
export const useSocketConnection = (): void => {
  const ctxProps = useGlobalStateContextSelectors({
    appLoading: selectAppLoading,
    dispatch: prop('dispatch'),
    socketState: prop('socketState'),
  });

  const handleSocketConnected = useStableCallback(() => {
    ctxProps.dispatch(setSocketState, { value: 'CONNECTED' });
  });

  const handleSocketDisconnected = useStableCallback(async (reason: string) => {
    if (reason !== 'ping timeout') {
      return;
    }
    // Prevent showing overlay when app is starting but only when app is logged in.
    if (!ctxProps.appLoading) {
      // Prevent occassional overlay for little while when socket disconnects and immediately reconnects
      await sleep(SOCKET_CONNECTION_DELAY);
      if (!isSocketConnected()) {
        ctxProps.dispatch(setSocketState, { value: 'DISCONNECTED' });
      }
    }
  });

  const handleSocketConnectionError = useStableCallback(async () => {
    if (!ctxProps.appLoading) {
      await sleep(SOCKET_CONNECTION_DELAY);
      if (isSocketConnected()) {
        return;
      }
    }
    ctxProps.dispatch(setSocketState, { value: 'DISCONNECTED' });
  });

  useEffect(() => {
    socketConnection(
      handleSocketConnected,
      handleSocketDisconnected,
      handleSocketConnectionError,
    );
    connectSocket();
    setTimeout(() => {
      ctxProps.dispatch(considerUninitializedSocketDisconnected);
    }, SOCKET_INITIAL_CONNECTION_TIMEOUT);
  }, [ctxProps.dispatch]);
};
